/**
 * The gardener's own face in the corner, or their initial where there is none.
 *
 * Only the photo uploaded on the profile page, or the initial. The picture the
 * sign-in provider (Google, Facebook) supplies is never used: Grow does not keep
 * it, so nobody else would see it, and the gardener must see what everyone else
 * sees (B114, owner). With no upload the letter on a disc is the prompt to add
 * one.
 *
 * The picture is not signed. Profile photographs live at a public path on the
 * CDN, which the profile page has always read directly — this reads the same
 * one, the same way, rather than inventing a second route to the same file.
 *
 * Used by: the Header's account button.
 *
 * @module UserAvatar
 */

import React from 'react';
import type { User } from 'firebase/auth';
import type { MySQLUser } from '@/shared/types/user';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { cn } from '@/lib/utils';

/** Whose face to draw, and at what size. */
interface UserAvatarProps {
  /** The Grow account: its uploaded photo and name come first. */
  mysqlUser: MySQLUser | null;
  /** The Firebase identity: its name and email, for the letter only (never its photo). */
  firebaseUser: User | null;
  /** Tailwind size classes; the header wants one size and dialogs may want another. */
  className?: string;
}

/**
 * The letter to fall back to.
 *
 * Their name, then the address they signed in with, then a question mark for
 * the moment before either has loaded.
 */
function initialFor(mysqlUser: MySQLUser | null, firebaseUser: User | null): string {
  const source = mysqlUser?.display_name
    || firebaseUser?.displayName
    || mysqlUser?.email
    || firebaseUser?.email
    || '';
  return source.trim().charAt(0).toUpperCase() || '?';
}

/** The gardener's uploaded photo, else their initial on a disc. */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  mysqlUser, firebaseUser, className,
}) => {
  /** Where photos are served from: the server's (limits.SPACES_BASE_URL). */
  const spaces = useGrowLimits().SPACES_BASE_URL;
  /*
   * A picture that 404s falls back to the letter rather than to a broken
   * image icon.
   *
   * profile_photo_url is a bare filename in the database, and the profile page
   * appends a cache-busting query to it after an upload, so the value can and
   * does change shape. Keying the failure on the resolved source means
   * replacing a photograph clears a previous failure instead of inheriting it.
   */
  const [failed, setFailed] = React.useState<string | null>(null);

  const stored = mysqlUser?.profile_photo_url;
  const source = stored && mysqlUser?.user_id && spaces
    ? `${spaces}/users/${mysqlUser.user_id}/${stored}`
    : null;

  const shape = cn('h-8 w-8 shrink-0 overflow-hidden rounded-full', className);

  if (source && failed !== source) {
    return (
      <img
        src={source}
        alt=""
        className={cn(shape, 'object-cover')}
        onError={() => setFailed(source)}
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        shape,
        'flex items-center justify-center font-serif font-semibold',
        // The theme's main colour: its words on a soft tint of it.
        'bg-primary/20 text-primary',
      )}
    >
      {initialFor(mysqlUser, firebaseUser)}
    </span>
  );
};
